import { AlertTriangle, ArrowLeft, Briefcase, Check, Flag, Play, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError } from '../../lib/api.ts';
import type { CardHole } from '../../lib/courses.ts';
import { getCourseCard } from '../../lib/courses.ts';
import type { Bag } from '../../lib/equipment.ts';
import { listBags } from '../../lib/equipment.ts';
import { useAuth } from '../../lib/auth.tsx';
import type { Friend } from '../../lib/friends.ts';
import { listFriends } from '../../lib/friends.ts';
import type { RoundState } from '../../lib/rounds.ts';
import { finishRound, getBag, getCurrentRound, startRound } from '../../lib/rounds.ts';

/* Le départ d'un tour.
 *
 * ── Un seul tour à la fois, et il faut le dire ─────────────────────────────
 *
 * Le serveur rend le tour déjà ouvert plutôt que d'en créer un second : on ne
 * joue pas deux parcours en même temps, et reprendre est ce qu'on veut après
 * avoir rangé le téléphone au trou 3.
 *
 * Mais si ce tour ouvert est sur un AUTRE golf, appuyer sur « Jouer » ici
 * mènerait à la carte d'ailleurs sans explication. L'écran vérifie donc
 * d'abord, et propose les deux issues honnêtes : reprendre ce tour, ou le
 * clore pour en ouvrir un ici.
 *
 * ── Le départ se choisit, il ne se devine pas ──────────────────────────────
 *
 * C'est lui qui fixe les distances du carnet, donc les suggestions de club. Le
 * proposer d'après la longueur de drive du joueur viendra ; en attendant, le
 * deviner donnerait des distances fausses sur les dix-huit trous.
 */

/* Ce que disent les couleurs.
 *
 * Un départ n'est pas un niveau de joueur, c'est une LONGUEUR. Plus la marque
 * est reculée, plus le parcours est long — et rien d'autre ne change. La
 * convention française est stable d'un club à l'autre, ce qui permet de la
 * dire une fois pour toutes.
 *
 * Les couleurs ne sont plus attachées à un sexe depuis longtemps : les rouges
 * sont la marque avancée, pas « la marque des dames ». Le dire autrement
 * enverrait des joueurs sur un parcours qui ne leur convient pas par gêne.
 */
const SENS_DES_DEPARTS: Record<string, string> = {
    noir: 'le plus long — compétition',
    blanc: 'long — compétition',
    jaune: 'la marque de référence',
    bleu: 'intermédiaire',
    rouge: 'avancée — le parcours le plus court',
    orange: 'très avancée — débutants et enfants',
};

function sensDe(label: string): string | null {
    const l = label.toLowerCase();
    const cle = Object.keys(SENS_DES_DEPARTS).find((k) => l.includes(k));
    return cle ? SENS_DES_DEPARTS[cle]! : null;
}

/* Quel départ conviendrait.
 *
 * Le critère n'est PAS le handicap, contrairement à l'intuition : c'est la
 * distance qu'on frappe. Deux joueurs de même index dont l'un porte 180 m et
 * l'autre 130 m n'ont rien à faire sur la même marque. La règle « Tee It
 * Forward » chiffre ça simplement — la longueur de parcours qui convient vaut
 * environ vingt-huit fois le coup de départ.
 *
 * On ne mesure pas encore les coups de départ, donc on prend le club le plus
 * long du sac comme approximation : sans driver, c'est bien lui qui part du
 * tee. L'approximation est annoncée à l'écran, et le joueur reste libre.
 */
const FACTEUR_TEE_IT_FORWARD = 28;

export function RoundStart() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [nom, setNom] = useState('');
    /* La carte d'identité du parcours : ville, nombre de trous, par total.
     *
     * Trois faits qu'on vérifie d'un coup d'œil avant de partir — est-ce le bon
     * golf, en fait-on neuf ou dix-huit, et à quel total se compare-t-on ?
     * Le par se somme depuis le carnet : c'est la seule source vraie, et un
     * parcours dont les trous ne sont pas saisis n'en affiche simplement pas. */
    const [carte, setCarte] = useState<{ trous: number; par: number | null }>(
        { trous: 0, par: null },
    );
    /* Les trous du carnet, gardés et non seulement résumés.
     *
     * Le par total suffisait pour l'affiche de départ ; l'écran de jeu, lui, a
     * besoin de chaque trou — son par, son index, sa distance depuis le départ
     * choisi. C'est ce qu'on lui transmet en ouvrant le tour, pour qu'il n'ait
     * pas à attendre que la file ait porté la création au serveur. */
    const [trousCarnet, setTrousCarnet] = useState<CardHole[]>([]);
    const [tees, setTees] = useState<{ id: string; label: string; longueurM: number | null }[]>([]);
    const [conseille, setConseille] = useState<string | null>(null);
    const [teeChoisi, setTeeChoisi] = useState<string | null>(null);
    const [amis, setAmis] = useState<Friend[]>([]);
    const [bags, setBags] = useState<Bag[]>([]);
    const [chosenBagId, setChosenBagId] = useState<string | null>(null);
    const [pickingBag, setPickingBag] = useState(false);
    /* Le partenaire amene depuis son profil est deja coche.
     *
     * C'est l'intention qu'on avait en cliquant « Un parcours » sur sa fiche :
     * la lui redemander ici serait lui faire refaire le geste qu'il vient de
     * faire. Il reste decochable, ce n'est pas une contrainte. */
    const [params] = useSearchParams();
    const [invites, setInvites] = useState<Set<string>>(() => {
        const v = params.get('inviter');
        return new Set(v ? [v] : []);
    });
    const [enCours, setEnCours] = useState<RoundState | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        void Promise.all([getCourseCard(id), listFriends(), getCurrentRound(), getBag(), listBags()])
            .then(([c, f, r, b, sacs]) => {
                setNom(c.course.name);
                setCarte({
                    trous: c.course.holeCount,
                    par: c.holes.some((h) => h.par != null)
                        ? c.holes.reduce((t, h) => t + (h.par ?? 0), 0)
                        : null,
                });
                setTrousCarnet(c.holes);
                setBags(sacs);
                // La longueur de chaque départ se lit dans le carnet : c'est la
                // seule mesure vraie, et elle vaut mieux qu'une convention.
                const longueurs = c.tees.map((t) => ({
                    id: t.id,
                    label: t.label,
                    longueurM: c.holes.reduce<number | null>((somme, h) => {
                        const d = h.distances[t.label];
                        return d == null ? somme : (somme ?? 0) + d;
                    }, null),
                }));
                setTees(longueurs);
                setAmis(f);
                if (r.round) setEnCours(r);

                // Le club le plus long tient lieu de coup de départ tant qu'on
                // ne le mesure pas. Ramené au nombre de trous du parcours :
                // neuf trous se comparent à la moitié d'un dix-huit.
                const plusLong = Math.max(0, ...b.clubs.map((k) => k.referenceDistanceM ?? 0));
                const cible = plusLong > 0
                    ? (plusLong * FACTEUR_TEE_IT_FORWARD * c.course.holeCount) / 18
                    : null;
                const mesures = longueurs.filter((t) => t.longueurM != null);
                const choix = cible != null && mesures.length > 0
                    // Le plus long qui reste sous la cible ; à défaut, le plus
                    // court du parcours — mieux vaut trop court que hors de
                    // portée sur les dix-huit trous.
                    ? (mesures.filter((t) => t.longueurM! <= cible)
                        .sort((a, b2) => b2.longueurM! - a.longueurM!)[0]
                        ?? mesures.sort((a, b2) => a.longueurM! - b2.longueurM!)[0])!.id
                    : null;
                setConseille(choix);
                setTeeChoisi(choix ?? c.tees[0]?.id ?? null);
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : 'Chargement impossible'))
            .finally(() => setLoading(false));
    }, [id]);

    // Le sac choisi, sinon le sac ACTIF — même repli qu'au practice. Sans
    // aucun sac composé, la section ne s'affiche pas et `bag` reste
    // `undefined` : le tour démarre quand même, sans filtrer les clubs.
    const bag = chosenBagId
        ? bags.find((b) => b.id === chosenBagId)
        : bags.find((b) => b.isActive) ?? bags[0];
    const usableBags = bags.filter((b) => b.clubs.length > 0);

    const demarrer = async (): Promise<void> => {
        if (!id) return;
        setBusy(true);
        setError(null);
        try {
            const depart = tees.find((t) => t.id === teeChoisi)?.label ?? null;
            await startRound({
                courseId: id, teeId: teeChoisi, friendIds: [...invites], bagId: bag?.id ?? null,
            }, {
                courseName: nom,
                holes: trousCarnet.map((h) => ({
                    number: h.number,
                    par: h.par,
                    strokeIndex: h.strokeIndex,
                    // La distance dépend du DÉPART : c'est la seule colonne du
                    // carnet qui change selon les repères d'où l'on joue.
                    distanceM: depart ? h.distances[depart] ?? null : null,
                })),
                teeLabel: depart,
                moi: {
                    userId: user?.id ?? '',
                    displayName: user?.displayName ?? '',
                    avatarUrl: user?.avatarUrl ?? null,
                },
                invites: amis.filter((f) => invites.has(f.id)).map((f) => ({
                    userId: f.id,
                    displayName: f.displayName,
                    avatarUrl: f.avatarUrl,
                })),
            });
            navigate('/parcours/jouer');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Impossible de démarrer');
        } finally { setBusy(false); }
    };

    const cloreEtDemarrer = async (): Promise<void> => {
        if (!enCours?.round) return;
        setBusy(true);
        try {
            await finishRound(enCours.round.id);
            setEnCours(null);
            await demarrer();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Impossible de terminer le tour en cours');
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    const ailleurs = enCours?.round != null && enCours.round.courseId !== id;

    return (
        <div className="space-y-4 py-2">
            <Link to="/parcours" className="btn btn-ghost btn-sm -mb-2 gap-2">
                <ArrowLeft size={16} /> Parcours
            </Link>

            {/* L'écusson au drapeau, puis le nom, puis les trois faits.
                On arrive ici depuis une liste de parcours qui se ressemblent
                tous : la première chose à vérifier est qu'on est au bon golf, et
                la seconde qu'on va bien jouer neuf ou dix-huit. Le par total
                situe le tour — c'est à lui que se comparera le score. */}
            <header className="flex items-center gap-3">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-box bg-[var(--fairway-deep)] text-accent shadow-md">
                    <Flag size={24} aria-hidden="true" />
                </span>
                <span className="min-w-0">
                    <h1 className="font-display truncate text-2xl font-bold leading-tight tracking-tight">
                        {nom}
                    </h1>
                    <p className="truncate text-[11px] font-bold uppercase tracking-[0.14em]"
                        style={{ color: 'var(--tuile-encre)' }}>
                        {[
                            carte.trous > 0 ? `${carte.trous} trous` : null,
                            carte.par != null ? `par ${carte.par}` : null,
                        ].filter(Boolean).join(' · ')}
                    </p>
                </span>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {/* Un tour déjà ouvert, ici ou ailleurs : on le dit avant que le
                joueur appuie sur un bouton qui ne fera pas ce qu'il croit. */}
            {enCours?.round && (
                <section className="rounded-box border border-warning bg-base-200 p-4">
                    <h2 className="flex items-center gap-2 text-sm font-semibold">
                        <AlertTriangle size={16} /> Un tour est déjà en cours
                    </h2>
                    <p className="mt-1 text-sm opacity-80">
                        {ailleurs
                            ? `Vous jouez actuellement à ${enCours.round.courseName}.`
                            : 'Vous avez un tour ouvert sur ce parcours.'}
                    </p>
                    <button type="button" onClick={() => navigate('/parcours/jouer')}
                        className="btn btn-primary btn-block mt-3 h-14 font-bold tap-target">
                        Reprendre ce tour
                    </button>
                    {ailleurs && (
                        <button type="button" disabled={busy} onClick={() => void cloreEtDemarrer()}
                            className="btn btn-outline btn-block mt-2 h-14 tap-target">
                            Le terminer et jouer ici
                        </button>
                    )}
                </section>
            )}

            {!enCours?.round && (
                <>
                    {/* Le départ : il fixe les distances, donc les suggestions. */}
                    <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                        <h2 className="mb-2 px-0.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: 'var(--tuile-encre)' }}>
                            Votre départ
                        </h2>
                        {tees.length === 0 ? (
                            <p className="mt-1 text-xs opacity-70">
                                Ce parcours n'a pas de départ saisi : les distances,
                                et donc les clubs proposés, ne seront pas disponibles.
                            </p>
                        ) : (
                            <>
                                {/* Une ligne par départ, pas des pastilles : il
                                    faut la place de dire ce que la couleur veut
                                    dire et ce qu'elle coûte en mètres. */}
                                {/* Une ligne par départ, pas des pastilles : il
                                    faut la place de dire ce que la couleur veut
                                    dire et ce qu'elle coûte en mètres.

                                    Le choisi prend le vert d'action, les autres
                                    le papier chaud des tuiles — la même paire
                                    que le sac et le pavé de coups. */}
                                <ul className="space-y-2">
                                    {tees.map((t) => (
                                        <li key={t.id}>
                                            <button type="button" onClick={() => setTeeChoisi(t.id)}
                                                aria-pressed={teeChoisi === t.id}
                                                style={teeChoisi === t.id ? undefined : {
                                                    backgroundColor: 'var(--tuile)',
                                                    color: 'var(--tuile-encre)',
                                                }}
                                                className={`flex w-full items-center justify-between gap-3 rounded-field p-3 text-left transition-transform active:scale-[0.99] tap-target ${
                                                    teeChoisi === t.id ? 'bg-primary text-primary-content shadow-md' : ''
                                                }`}>
                                                <span className="min-w-0">
                                                    <span className="flex items-center gap-2">
                                                        <span className="font-score text-xl leading-[1.15]">{t.label}</span>
                                                        {conseille === t.id && (
                                                            <span className="flex shrink-0 items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-accent-content">
                                                                <Sparkles size={11} aria-hidden="true" /> conseillé
                                                            </span>
                                                        )}
                                                    </span>
                                                    {sensDe(t.label) && (
                                                        <span className="mt-0.5 block text-xs font-semibold opacity-70">
                                                            {sensDe(t.label)}
                                                        </span>
                                                    )}
                                                </span>
                                                {t.longueurM != null && (
                                                    <span className="font-score shrink-0 text-right text-xl leading-none tabular-nums">
                                                        {t.longueurM} m
                                                    </span>
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                {conseille && (
                                    <p className="mt-2 px-0.5 text-xs opacity-60">
                                        Le conseil suit la longueur que vous frappez, pas votre index.
                                    </p>
                                )}
                            </>
                        )}
                    </section>

                    {/* Le sac qui accompagne le tour.
                     *
                     * Absente sans aucun sac composé : imposer d'en créer un
                     * avant de pouvoir jouer serait bloquer un tour pour un
                     * réglage que personne n'a demandé. Dès qu'un sac existe,
                     * le choisir ici évite de le découvrir a posteriori sur
                     * le premier départ, quand il est trop tard pour changer
                     * d'avis sans revenir en arrière. */}
                    {usableBags.length > 0 && bag && (
                        <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                            {/* Même habillage que les deux autres blocs : trois
                                cartes blanches et trois titres en capitales,
                                parce que ce sont trois choix du même rang. Le
                                gris d'avant en faisait un réglage secondaire,
                                alors que jouer avec le mauvais sac se paie sur
                                les dix-huit trous. */}
                            <h2 className="mb-2 px-0.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                                style={{ color: 'var(--tuile-encre)' }}>
                                Votre sac
                            </h2>
                            <div className="mt-2 flex items-center justify-between gap-3 rounded-box bg-base-100 px-4 py-3">
                                <span className="min-w-0 text-sm">
                                    <span className="block truncate font-semibold">{bag.name}</span>
                                    <span className="block text-xs opacity-60">
                                        {bag.clubs.length} club{bag.clubs.length > 1 ? 's' : ''}
                                    </span>
                                </span>
                                {usableBags.length > 1 && (
                                    <button type="button" className="btn btn-ghost btn-xs shrink-0"
                                        onClick={() => setPickingBag((v) => !v)}>
                                        {pickingBag ? 'Fermer' : 'Changer'}
                                    </button>
                                )}
                            </div>

                            {pickingBag && (
                                <div className="mt-2 space-y-1">
                                    {usableBags.map((b) => (
                                        <button key={b.id} type="button"
                                            onClick={() => { setChosenBagId(b.id); setPickingBag(false); }}
                                            className={`flex w-full items-center justify-between gap-3 rounded-field px-3 py-2 text-left text-sm transition-colors ${
                                                b.id === bag.id ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                            }`}>
                                            <span className="min-w-0">
                                                <span className="block truncate font-semibold">{b.name}</span>
                                                {b.description && (
                                                    <span className="block truncate text-xs opacity-70">{b.description}</span>
                                                )}
                                            </span>
                                            <span className="shrink-0 text-xs opacity-70">
                                                {b.clubs.length} club{b.clubs.length > 1 ? 's' : ''}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <Link to="/materiel" className="btn btn-ghost btn-xs mt-2 gap-2">
                                <Briefcase size={14} /> Composer mes sacs
                            </Link>
                        </section>
                    )}

                    {/* Toujours visible, meme sans partenaire : cachee, elle
                        laissait croire que l'application ne sait pas jouer a
                        plusieurs. Vide, elle dit ou aller en chercher. */}
                    {(
                        <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                            <h2 className="px-0.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                                style={{ color: 'var(--tuile-encre)' }}>
                                Avec qui ?
                            </h2>
                            {amis.length > 0 && (
                                <p className="mb-2 mt-1 px-0.5 text-xs opacity-60">
                                    Ils recevront l'invitation dans l'application. Chacun choisit son départ.
                                </p>
                            )}
                            {amis.length === 0 && (
                                <p className="mt-2 text-sm opacity-70">
                                    Vous n'avez pas encore de partenaire.{' '}
                                    <Link to="/putting/amis" className="link font-semibold">
                                        En inviter un
                                    </Link>{' '}
                                    prend un lien et sept jours.
                                </p>
                            )}
                            <ul className="space-y-2">
                                {amis.map((f) => (
                                    <li key={f.id}>
                                        <button type="button"
                                            aria-pressed={invites.has(f.id)}
                                            onClick={() => setInvites((s) => {
                                                const n = new Set(s);
                                                if (n.has(f.id)) n.delete(f.id); else n.add(f.id);
                                                return n;
                                            })}
                                            style={invites.has(f.id) ? undefined : {
                                                backgroundColor: 'var(--tuile)',
                                                color: 'var(--tuile-encre)',
                                            }}
                                            className={`flex w-full items-center gap-3 rounded-field p-2.5 transition-transform active:scale-[0.99] tap-target ${
                                                invites.has(f.id) ? 'bg-primary text-primary-content shadow-md' : ''
                                            }`}>
                                            <Avatar name={f.displayName} email={f.email}
                                                avatarUrl={f.avatarUrl} size={36} />
                                            <span className="min-w-0 flex-1 truncate text-left font-bold">
                                                {f.displayName}
                                            </span>
                                            {/* L'index à droite, en jeton : c'est ce
                                                qu'on regarde en composant une
                                                partie, et il se compare de haut en
                                                bas quand tous sont alignés. */}
                                            {f.handicapIndex != null && (
                                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${
                                                    invites.has(f.id) ? 'bg-primary-content/20' : 'bg-black/[0.07]'}`}>
                                                    {f.handicapIndex.toFixed(1).replace('.', ',')}
                                                </span>
                                            )}
                                            {invites.has(f.id) && (
                                                <Check size={18} strokeWidth={3} aria-hidden="true" className="shrink-0" />
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* « Lancer la partie » et non « démarrer le parcours » : le
                        parcours est le lieu, il ne démarre pas. C'est la partie
                        qui commence, et c'est le mot qu'on emploie au départ. */}
                    <button type="button" disabled={busy} onClick={() => void demarrer()}
                        className="btn btn-primary btn-block h-16 gap-2 text-lg font-bold shadow-md tap-target">
                        <Play size={22} /> Lancer la partie
                    </button>
                </>
            )}

            <Link to={`/parcours/${id}/carnet`} className="btn btn-ghost btn-block gap-2 tap-target">
                <Flag size={16} /> Voir le carnet
            </Link>
        </div>
    );
}
